import { Button, Grid, Modal } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, Trash2 } from "lucide-react";
import type React from "react";
import { memo, useMemo } from "react";

const { useBreakpoint } = Grid;

interface ClearJobConfirmationProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  fileName?: string;
  loading?: boolean;
}

// Memoize static motion variants
const motionVariants = {
  container: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: 0.2 },
  },
  icon: {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { delay: 0.1 },
  },
};

// Extract IconContainer to prevent re-renders
const IconContainer = memo(({ isMobile }: { isMobile: boolean }) => (
  <motion.div {...motionVariants.icon}>
    <div className={`bg-red-100 rounded-full ${isMobile ? "p-2 mb-3" : "p-3 mb-4"}`}>
      <AlertCircle className={`${isMobile ? "w-6 h-6" : "w-8 h-8"} text-red-600`} />
    </div>
  </motion.div>
));
IconContainer.displayName = "IconContainer";

// Extract ButtonContainer to prevent re-renders
const ButtonContainer = memo(
  ({
    isMobile,
    onCancel,
    onConfirm,
    loading,
  }: {
    isMobile: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    loading: boolean;
  }) => (
    <div className={`flex gap-3 w-full ${isMobile ? "flex-col" : "flex-row"}`}>
      <Button
        onClick={onCancel}
        className={`${isMobile ? "h-9 order-2" : "h-10"} hover:bg-gray-100`}
        block
      >
        Cancel
      </Button>
      <Button
        type="primary"
        danger
        onClick={onConfirm}
        loading={loading}
        icon={<Trash2 className={`${isMobile ? "w-3.5 h-3.5" : "w-4 h-4"}`} />}
        className={`${isMobile ? "h-9 order-1" : "h-10"} items-center justify-center flex gap-2`}
        block
      >
        Clear Job
      </Button>
    </div>
  ),
);
ButtonContainer.displayName = "ButtonContainer";

const ClearJobConfirmation: React.FC<ClearJobConfirmationProps> = memo(
  ({ isOpen, onConfirm, onCancel, fileName = "", loading = false }) => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    // Memoize modal style
    const modalStyle = useMemo(
      () => ({
        top: isMobile ? "35%" : "20%",
      }),
      [isMobile],
    );

    // Memoize modal props
    const modalProps = useMemo(
      () => ({
        title: null,
        open: isOpen,
        footer: null,
        onCancel,
        width: isMobile ? "94%" : 400,
        style: modalStyle,
        className: "rounded-lg overflow-hidden",
        maskTransitionName: "",
        transitionName: "",
        centered: isMobile,
      }),
      [isOpen, onCancel, isMobile, modalStyle],
    );

    return (
      <AnimatePresence mode="wait">
        {isOpen && (
          <Modal {...modalProps}>
            <motion.div
              {...motionVariants.container}
              className={`flex flex-col items-center text-center ${isMobile ? "px-3 pt-4 pb-3" : "px-4 pt-6 pb-4"}`}
            >
              <IconContainer isMobile={isMobile} />

              <h3
                className={`font-semibold text-gray-900 mb-2 ${isMobile ? "text-base" : "text-lg"}`}
              >
                Clear Job Confirmation
              </h3>

              <p className={`text-gray-600 ${isMobile ? "mb-4 text-sm" : "mb-6 text-base"}`}>
                Are you sure you want to clear{" "}
                {fileName && (
                  <span className="font-medium text-gray-900">&ldquo;{fileName}&rdquo;</span>
                )}
                ?{!isMobile && <br />}
                This action cannot be undone.
              </p>

              <ButtonContainer
                isMobile={isMobile}
                onCancel={onCancel}
                onConfirm={onConfirm}
                loading={loading}
              />
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>
    );
  },
);

ClearJobConfirmation.displayName = "ClearJobConfirmation";

export default ClearJobConfirmation;
